import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown } from 'react-icons/fa';

export interface SearchableDropdownOption {
	value: string;
	label: string;
}

interface SearchableDropdownProps {
	options: SearchableDropdownOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	disabled?: boolean;
	className?: string;
	onClickOutside?: () => void;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
	options,
	value,
	onChange,
	placeholder = "Select...",
	searchPlaceholder = "Search...",
	disabled = false,
	className = "",
	onClickOutside,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				onClickOutside?.();
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [isOpen, onClickOutside]);

	const filteredOptions = options.filter(option =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const selectedOption = options.find(option => option.value === value);

	const handleOptionClick = (optionValue: string) => {
		onChange(optionValue);
		setIsOpen(false);
		setSearchTerm('');
	};

	const handleToggle = () => {
		if (!disabled) {
			setIsOpen(!isOpen);
			if (!isOpen) {
				setSearchTerm('');
			}
		}
	};

	const baseStyles = "w-full px-2 py-1.5 text-xs text-primary-text border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-primary min-h-[32px] flex items-center justify-between";
	const disabledStyles = "cursor-not-allowed bg-gray-100";
	const enabledStyles = "cursor-pointer bg-white";

	return (
		<div className={`relative ${className}`} ref={dropdownRef}>
			<div
				onClick={handleToggle}
				className={`${baseStyles} ${
					disabled ? disabledStyles : enabledStyles
				}`}
			>
				{selectedOption ? (
					<span className="truncate">{selectedOption.label}</span>
				) : (
					<span className="text-gray-400">{placeholder}</span>
				)}
				<FaChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
			</div>

			{isOpen && !disabled && (
				<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-hidden">
					{/* Search input */}
					<div className="p-2 border-b border-gray-200">
						<input
							type="text"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder={searchPlaceholder}
							className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
							onClick={(e) => e.stopPropagation()}
							autoFocus
						/>
					</div>
					
					{/* Options list */}
					<div className="max-h-32 overflow-y-auto">
						{filteredOptions.length > 0 ? (
							filteredOptions.map((option) => (
								<div
									key={option.value}
									onClick={() => handleOptionClick(option.value)}
									className="px-2 py-1.5 hover:bg-blue-50 cursor-pointer text-xs text-primary-text"
								>
									{option.label}
								</div>
							))
						) : (
							<div className="px-2 py-1.5 text-xs text-gray-500 text-center">
								No matching options
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default SearchableDropdown;
